import type { UseFormReturn } from "react-hook-form";
import type { bestillerSelskapFormValues } from "../bestillinger/forms";
import { useEffectWithPrevious } from "./useEffectWithPrevious";

export const useSelskapsBoligType = (
  form: UseFormReturn<bestillerSelskapFormValues>,
) => {
  const isNæringsbyggMedBoliger = form.watch("boligType") === "Andre";
  const isNæringsdrivende =
    form.watch("næringsdrivende") === "ja" || isNæringsbyggMedBoliger;

  const boligType = form.watch("boligType");

  // Reset dependent fields when boligType changes to force the user to
  // re-evaluate their choices based on the new boligType
  // NB!: Might compare state from two different renders/form instances if not careful, for example:
  // 1. Component renders with initial form values and useSelskapsBoligType is called
  // 2. Component renders again with new form values (e.g. after a refetch) and useSelskapsBoligType is called
  // In this case, prev would be boligType from the initial form instance and curr would be boligType from the new form instance
  // Force remount when props change to avoid this
  useEffectWithPrevious((prev, curr) => {
    // Ignore first render, form intializing and same value changes
    if (prev == null || prev === curr) {
      return;
    }

    if (curr === "Andre") {
      form.setValue("antallBoenheter", undefined);
      form.clearErrors("antallBoenheter");
      form.setValue("næringsarealProsent", undefined);
      form.clearErrors("næringsarealProsent");
      return;
    }

    if (curr === "Boligselskap") {
      form.setValue("antallBoenheter", undefined);
      form.clearErrors("antallBoenheter");
      form.setValue("næringsarealProsent", undefined);
      form.clearErrors("næringsarealProsent");
      form.setValue("næringsdrivende", undefined);
      form.clearErrors("næringsdrivende");
      return;
    }
  }, boligType);

  // Reset næringsarealProsent when isNæringsdrivende changes to prevent dangling form errors
  // Also forces the user to reevalutae næringsarealProsent when switching between boligType and isNæringsdrivende
  useEffectWithPrevious((prev, curr) => {
    // Ignore first render, form intializing and same value changes
    if (prev == null || prev === curr) {
      return;
    }
    form.setValue("næringsarealProsent", undefined);
    form.clearErrors("næringsarealProsent");
  }, isNæringsdrivende);

  const formDataIsNæringsdrivende = (
    bestillerSelskapFormValues: bestillerSelskapFormValues,
  ) => {
    return (
      bestillerSelskapFormValues.næringsdrivende === "ja" ||
      formDataIsNæringsbyggMedBoliger(bestillerSelskapFormValues)
    );
  };

  const formDataIsNæringsbyggMedBoliger = (
    bestillerSelskapFormValues: bestillerSelskapFormValues,
  ) => {
    return bestillerSelskapFormValues.boligType === "Andre";
  };
  return {
    isNæringsdrivende,
    isNæringsbyggMedBoliger,
    formDataIsNæringsdrivende,
    formDataIsNæringsbyggMedBoliger,
  };
};
